<template>
  <div class="wrraper">
    <!-- Start Breadcrumb -->
    <Breadcrumb
      :items="items"
      search_route="/district/add"
      :search_title="lang === 'ar' ? 'إضافة حي جديد' : 'Add New District'"
      icon="fa-plus"
    />
    <!-- End Breadcrumb -->

    <!-- Filters Section -->
    <div class="district-filter-card mb-4">
      <div class="row align-items-center">
        <!-- Keyword Search -->
        <div class="col-md-5 col-lg-4 py-2">
          <div class="input_wrapper top_label mb-0">
            <input
              type="text"
              class="form-control"
              v-model="searchKeyword"
              :placeholder="
                lang === 'ar'
                  ? 'بحث باسم الحي...'
                  : 'Search by district name...'
              "
              @keyup.enter="handleFilterChange"
            />
            <label class="form-label">
              <i class="fas fa-search me-1"></i>
              {{ lang === "ar" ? "بحث" : "Search" }}
            </label>
          </div>
        </div>

        <!-- City Filter -->
        <div class="col-md-5 col-lg-4 py-2">
          <div class="input_wrapper top_label mb-0">
            <label class="form-label">
              <i class="fas fa-city me-1"></i>
              {{ $t("forms.labels.city") }}
            </label>
            <multiselect
              v-model="selectedCity"
              :options="cities"
              label="name"
              track-by="id"
              :placeholder="
                lang === 'ar' ? 'تصفية حسب المدينة' : 'Filter by city'
              "
              :searchable="true"
              :allow-empty="true"
              :show-labels="false"
              @input="handleFilterChange"
            ></multiselect>
          </div>
        </div>

        <!-- Filter Action Buttons -->
        <div class="col-md-2 col-lg-4 py-2 d-flex align-items-center gap-2">
          <button
            type="button"
            class="btn btn-primary filter-btn px-4"
            @click="handleFilterChange"
          >
            <i class="fas fa-filter me-1"></i>
            {{ lang === "ar" ? "تصفية" : "Filter" }}
          </button>
          <button
            type="button"
            class="btn btn-outline-secondary reset-btn px-3"
            v-if="selectedCity || searchKeyword"
            @click="resetFilters"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Start Main Section -->
    <main class="magic-table-wrapper">
      <v-data-table
        class="thumb strip"
        :headers="headers"
        :items="rows"
        :loading="lodaing"
        :loading-text="$t('table.loadingData')"
        item-key="id"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- ID -->
        <template v-slot:[`item.id`]="{ item }">
          <span class="font-weight-bold text-muted">#{{ item.id }}</span>
        </template>

        <!-- District Name -->
        <template v-slot:[`item.name`]="{ item }">
          <div class="d-flex align-items-center gap-2">
            <span class="district-name-cell font-weight-bold">
              {{
                item.name ||
                (lang === "ar" ? item.ar?.name : item.en?.name) ||
                "---"
              }}
            </span>
          </div>
        </template>

        <!-- City Name -->
        <template v-slot:[`item.city.name`]="{ item }">
          <span class="badge badge-city" v-if="item.city">
            <i class="fas fa-map-marker-alt me-1 text-primary"></i>
            {{ item.city.name }}
          </span>
          <span v-else class="text-muted">---</span>
        </template>

        <!-- Area Points Count Badge -->
        <template v-slot:[`item.area`]="{ item }">
          <span class="badge badge-area" v-if="item.area && item.area.length">
            <i class="fas fa-draw-polygon me-1"></i>
            {{ item.area.length }} {{ lang === "ar" ? "نقاط" : "pts" }}
          </span>
          <span v-else class="text-muted">---</span>
        </template>

        <!-- IsActive -->
        <template v-slot:[`item.is_active`]="{ item }">
          <span class="status" :class="item.is_active ? 'paid' : 'unpaid'">
            {{
              item.is_active
                ? lang === "ar"
                  ? "مفعل"
                  : "Active"
                : lang === "ar"
                ? "معطل"
                : "Inactive"
            }}
          </span>
        </template>

        <!-- Created At -->
        <template v-slot:[`item.created_at`]="{ item }">
          <span class="created-date" v-if="item.created_at">
            {{ formatDate(item.created_at) }}
          </span>
          <span v-else class="text-muted">---</span>
        </template>

        <!-- Select no data State -->
        <template v-slot:no-data>
          {{ $t("table.noData") }}
        </template>

        <!-- Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="_actions">
            <v-icon
              class="edit"
              small
              @click="editItem(item)"
              v-if="helper_hasPermission('edit')"
            >
              fal fa-edit
            </v-icon>
            <v-icon
              class="delete"
              small
              @click="deleteItem(item)"
              v-if="helper_hasPermission('delete')"
            >
              fal fa-trash
            </v-icon>
          </div>
        </template>

        <!-- Table Header Title -->
        <template v-slot:top>
          <div
            class="d-flex align-items-center justify-content-between flex-wrap p-3"
          >
            <h3 class="title table-title mb-0">
              {{ $t("breadcrumb.districts.title") }}
              <span
                class="badge bg-purple text-white ms-2"
                v-if="statisticsItem !== null"
              >
                {{ statisticsItem }}
              </span>
            </h3>
          </div>

          <!-- Delete dialog -->
          <v-dialog v-model="dialogDelete" max-width="500px">
            <v-card>
              <v-card-title class="text-h5 justify-center">
                {{ $t("table.deletedialog.areYouSure") }}
              </v-card-title>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="#1B5E20" text @click="deleteItemConfirm">
                  {{ $t("table.deletedialog.ok") }}
                </v-btn>
                <v-btn color="#F44336" text @click="dialogDelete = false">
                  {{ $t("table.deletedialog.cancel") }}
                </v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </template>
      </v-data-table>
    </main>
    <!-- End Main Section -->

    <!-- Start Pagination -->
    <div
      class="pagination_container text-center mb-5 d-flex justify-content-end"
      v-if="paginations.last_page > 1"
    >
      <v-pagination
        v-model.number="paginations.current_page"
        :length="paginations.last_page"
        :total-visible="5"
        @input="fetchData($event)"
      ></v-pagination>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "DistrictShowAll",

  data() {
    return {
      // ========== Breadcrumbs
      items: [
        {
          text: this.$t("breadcrumb.mainPage"),
          disabled: false,
          href: "/",
        },
        {
          text: this.$t("breadcrumb.districts.title"),
          disabled: false,
          href: "/district/show-all",
        },
        {
          text: this.$t("breadcrumb.districts.all"),
          disabled: true,
          href: "",
        },
      ],

      // ========== Statistics
      statisticsItem: null,

      // ========== Filter state
      searchKeyword: "",
      selectedCity: null,
      cities: [],

      // ========== dialog Status
      dialogDelete: false,
      itemtoDelete: null,

      // ========== Your Data
      rows: [],

      // ========== pagination
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 20,
      },

      // ========== Loading
      lodaing: false,
    };
  },

  computed: {
    ...mapGetters({
      lang: "lang_module/lang",
    }),

    headers() {
      if (this.lang === "ar") {
        return [
          { text: "#", align: "center", value: "id", sortable: false },
          { text: "اسم الحي", align: "center", value: "name", sortable: false },
          {
            text: "المدينة",
            align: "center",
            value: "city.name",
            sortable: false,
          },
          {
            text: "حدود المضلع",
            align: "center",
            value: "area",
            sortable: false,
          },
          {
            text: "وصف الموقع",
            align: "center",
            value: "location",
            sortable: false,
          },
          {
            text: "الحالة",
            align: "center",
            value: "is_active",
            sortable: false,
          },
          {
            text: "تاريخ الإنشاء",
            align: "center",
            value: "created_at",
            sortable: false,
          },
          {
            text: "التحكم",
            value: "actions",
            sortable: false,
            align: "center",
          },
        ];
      } else {
        return [
          { text: "#", align: "center", value: "id", sortable: false },
          {
            text: "District Name",
            align: "center",
            value: "name",
            sortable: false,
          },
          {
            text: "City",
            align: "center",
            value: "city.name",
            sortable: false,
          },
          {
            text: "Polygon Area",
            align: "center",
            value: "area",
            sortable: false,
          },
          {
            text: "Location Description",
            align: "center",
            value: "location",
            sortable: false,
          },
          {
            text: "Status",
            align: "center",
            value: "is_active",
            sortable: false,
          },
          {
            text: "Created At",
            align: "center",
            value: "created_at",
            sortable: false,
          },
          {
            text: "Actions",
            value: "actions",
            sortable: false,
            align: "center",
          },
        ];
      }
    },
  },

  methods: {
    getCities() {
      this.$axios({
        method: "GET",
        url: "city_not_paginated",
      })
        .then((res) => {
          this.cities = res.data.data.map((item) => ({
            id: item.id,
            name: item.name,
          }));
          if (this.$route.query.city_id) {
            const cityId = +this.$route.query.city_id;
            this.selectedCity =
              this.cities.find((c) => c.id === cityId) || null;
          }
        })
        .catch(() => {});
    },

    formatDate(dateStr) {
      if (!dateStr) return "---";
      try {
        const d = new Date(dateStr);
        return d.toLocaleDateString(this.lang === "ar" ? "ar-SA" : "en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch {
        return dateStr;
      }
    },

    handleFilterChange() {
      this.paginations.current_page = 1;
      const query = {
        ...this.$route.query,
        page: 1,
      };

      if (this.searchKeyword) {
        query.keyword = this.searchKeyword;
      } else {
        delete query.keyword;
      }

      if (this.selectedCity && this.selectedCity.id) {
        query.city_id = this.selectedCity.id;
      } else {
        delete query.city_id;
      }

      this.$router.replace({ query }).catch(() => {});
      this.setRows();
    },

    resetFilters() {
      this.searchKeyword = "";
      this.selectedCity = null;
      this.handleFilterChange();
    },

    editItem(item) {
      this.$router.push({ path: `/district/edit/${item.id}` });
    },

    deleteItem(item) {
      this.dialogDelete = true;
      this.itemtoDelete = item;
    },

    deleteItemConfirm() {
      if (!this.itemtoDelete) return;
      this.$axios({
        method: "DELETE",
        url: `district/${this.itemtoDelete.id}`,
      })
        .then(() => {
          this.rows = this.rows.filter(
            (item) => item.id !== this.itemtoDelete.id
          );
          this.dialogDelete = false;
          if (this.statisticsItem !== null) this.statisticsItem--;
          this.$iziToast.success({
            timeout: 2000,
            message: this.$t("deleteSuccess"),
            position: "bottomRight",
          });
        })
        .catch((err) => {
          this.dialogDelete = false;
          this.$iziToast.error({
            timeout: 2000,
            message: err.response?.data?.message || "Error deleting district",
            position: "bottomRight",
          });
        });
    },

    setRows() {
      this.lodaing = true;
      const params = {
        page: this.paginations.current_page,
      };

      if (this.searchKeyword) {
        params.keyword = this.searchKeyword;
      } else if (this.$route.query.keyword) {
        params.keyword = this.$route.query.keyword;
      }

      if (this.selectedCity && this.selectedCity.id) {
        params.city_id = this.selectedCity.id;
      } else if (this.$route.query.city_id) {
        params.city_id = this.$route.query.city_id;
      }

      this.$axios({
        method: "GET",
        url: "district",
        params,
      })
        .then((res) => {
          if (res.data.meta) {
            this.paginations.last_page = res.data.meta.last_page || 1;
            this.paginations.items_per_page = res.data.meta.per_page || 20;
            this.statisticsItem = res.data.meta.total;
          }
          this.rows = res.data.data || [];
          this.lodaing = false;
        })
        .catch(() => {
          this.lodaing = false;
        });
    },

    fetchData(e) {
      this.$router
        .replace({ query: { ...this.$route.query, page: e } })
        .catch(() => {});
      this.setRows();
    },
  },

  created() {
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    if (this.$route.query.keyword) {
      this.searchKeyword = this.$route.query.keyword;
    }
    this.getCities();
    this.setRows();
  },
};
</script>

<style lang="scss" scoped>
.district-filter-card {
  background: #ffffff;
  padding: 16px 20px;
  border-radius: 12px;
  border: 1px solid rgba(120, 61, 115, 0.12);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);

  .filter-btn {
    background: #783d73;
    border-color: #783d73;
    color: #ffffff;
    font-weight: 600;
    height: 42px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:hover {
      background: #5d2e59;
      border-color: #5d2e59;
    }
  }

  .reset-btn {
    height: 42px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}

.badge-city {
  background: #f0f4ff;
  color: #1e40af;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12.5px;
}

.badge-area {
  background: #fdf2f8;
  color: #9d174d;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
}

.district-name-cell {
  color: #1e293b;
  font-size: 14px;
}

.created-date {
  font-size: 12.5px;
  color: #64748b;
}

.bg-purple {
  background-color: #783d73 !important;
}
</style>
